import { Fix64 } from "../maths/fix64";
import { positionAt } from "../world/roadGeometry";
import type { Enemy } from "./entities";
import type { LevelSim } from "./levelSim";
import { RaiderAt, biteTarget } from "./petFighter";
import { strikeTarget } from "./petStrike";
import { enemyStep, warlordReach2, warlordsOf } from "./systems/enemyMotion";
import { standsStill } from "./systems/enemyAbilities";

/** The pet's next attack as the sim stands now: the raider it would pick and the ticks until it may strike. */
export interface PetForecast {
  /** The raider's id, -1 when none is in reach. */
  readonly target: number;
  /** Ticks until the attack may strike: it strikes on the tick after its cooldown reads 0 (1: the next tick). */
  readonly inTicks: number;
  /** The attack's reach round the flag's cell (cells) and its cadence (ticks between strikes). */
  readonly reach: Fix64;
  readonly intervalTicks: number;
}

/**
 * Read-only: the pet's next attack (Bite, sim/petFighter.ts, or a Pets v3 strike, sim/petStrike.ts) from the sim's
 * state now, picked by the very function the attack uses — so the presentation can stage it ahead of the tick and
 * the sim stays the one source of the choice. `extra` widens the reach (a raider still walking in; 0: exactly the
 * sim's choice if it struck now). Null in a run without a pet attack. It reads and never writes: no RNG, no events.
 */
export function petForecast(sim: LevelSim, extra: Fix64 = Fix64.Zero): PetForecast | null {
  const pet = sim.petSystem, rule = pet?.rule;
  if (!pet || !rule) return null;
  if (rule.bite) {
    const reach = rule.bite.reach;
    return { target: biteTarget(sim, reach.add(extra))?.id ?? -1, inTicks: pet.biteCooldown + 1, reach, intervalTicks: rule.bite.intervalTicks };
  }
  const s = rule.strike;
  if (!s) return null;
  return { target: strikeTarget(sim, s.reach.add(extra), s.targets)?.id ?? -1, inTicks: pet.strikeCooldown + 1, reach: s.reach, intervalTicks: s.intervalTicks };
}

/** The raider the pet will strike first and the tick (`inTicks` ticks on), seen ahead (petArrival). */
export interface PetArrival { readonly target: number; readonly inTicks: number }

/**
 * Read-only: before any raider is in reach, which one the attack will strike first and when — each raider carried along
 * the road at the step it takes now (sim/systems/enemyMotion.ts enemyStep: its speed, slows, boosts, mud, a Warlord's
 * aura; a raider held by the troops, stunned or standing still stays put), and at each tick from the first the attack
 * may strike, the attack's own choice (biteTarget / strikeTarget) over those positions. Up to `maxTicks` ahead; null
 * when none would be in reach by then, or in a run without a pet attack. It reads and never writes.
 *
 * What it cannot see: a raider the troops will only start holding later (it is carried on as if they let it pass), a
 * new spawn, a slow or stun yet to land. The pet's presentation stages its first blow from it (packages/web petFoes.ts).
 */
export function petArrival(sim: LevelSim, maxTicks: number): PetArrival | null {
  const pet = sim.petSystem, rule = pet?.rule;
  if (!pet || !rule) return null;
  const bite = rule.bite, strike = rule.strike;
  if (!bite && !strike) return null;
  const first = (bite ? pet.biteCooldown : pet.strikeCooldown) + 1;
  const warlords = warlordsOf(sim), wr2 = warlordReach2(), cells = sim.road.roadCells, length = sim.road.length;
  // each raider's step now; one that cannot come within reach in `maxTicks` even straight at the flag is left out
  const reach = (bite ?? strike!).reach, r = sim.troops.rally, fx = Fix64.fromInt(r.x), fy = Fix64.fromInt(r.y);
  const steps = new Map<number, Fix64>();
  for (const e of sim.enemySlots) {
    if (!e.alive) continue;
    const step = e.blocked || e.stunTicksLeft > 0 || standsStill(e) ? Fix64.Zero : enemyStep(sim, e, warlords, wr2);
    const far = reach.add(step.mulInt(maxTicks)), dx = e.x.sub(fx), dy = e.y.sub(fy);
    if (dx.mul(dx).add(dy.mul(dy)).le(far.mul(far))) steps.set(e.id, step);
  }
  const p = { x: Fix64.Zero, y: Fix64.Zero, cx: 0, cy: 0 };
  let k = first;
  const at: RaiderAt = (e: Enemy) => {
    const step = steps.get(e.id);
    if (step === undefined) return null;                          // too far to matter
    if (step.isZero()) return e;
    const d = e.distance.add(step.mulInt(k));
    if (d.ge(length)) return null;                                  // it has left the road (at the gate) by then
    [p.x, p.y] = positionAt(cells, d);
    p.cx = p.x.floorToInt(); p.cy = p.y.floorToInt();
    return p;
  };
  for (; k <= maxTicks; k++) {
    const t = bite ? biteTarget(sim, bite.reach, at) : strikeTarget(sim, strike!.reach, strike!.targets, at);
    if (t) return { target: t.id, inTicks: k };
  }
  return null;
}
